/**
 * 添付のサムネイル表示(2026-10-14 発注者指示: 詳細などで添付を開いたとき、画像を大きめに見せる)。
 *
 * - 画像(PNG・JPEG など)はそのまま縮小して表示する
 * - PDFは1ページ目を縮小して表示する(全画面表示と同じ pdf.js を使う)
 * - iPhone形式(HEIC)など、ブラウザが表示できないものは種類の印だけ出す(押せば従来どおり開く)
 * - 押すと従来の全画面表示(拡大・ページ送り)を開く
 *
 * 縦長の領収書・A4の請求書が切れずに見えるよう、枠は縦長(3:4)にして全体を収める。
 */
import { useEffect, useRef, useState } from 'react';
import { getFileUrl } from './attachments';
import FilePreviewModal from './FilePreviewModal';
import { PDFJS_ASSETS } from '@core/components/PdfView';
import type { StoredFile } from '@core/lib/types';

/** サムネイルの描画幅(表示幅の約2倍。高精細な画面でもぼやけないように) */
const PDF_THUMB_WIDTH = 360;

type PdfPage = {
  getViewport: (o: { scale: number }) => { width: number; height: number };
  render: (o: { canvas: HTMLCanvasElement; canvasContext: CanvasRenderingContext2D; viewport: unknown }) => {
    promise: Promise<void>;
    cancel: () => void;
  };
};
type PdfDoc = { getPage: (n: number) => Promise<PdfPage>; destroy?: () => void };

function PdfThumb({ url, onError }: { url: string; onError: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  /** 描き終わるまでは「読み込み中…」を出す(空の枠のままだと壊れて見えるため) */
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setReady(false);
    let doc: PdfDoc | null = null;
    let task: { cancel: () => void } | null = null;
    (async () => {
      const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
      const worker = await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url');
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
      doc = (await pdfjs.getDocument({ url, ...PDFJS_ASSETS }).promise) as unknown as PdfDoc;
      if (cancelled) return;
      const page = await doc.getPage(1);
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: PDF_THUMB_WIDTH / base.width });
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx || cancelled) return;
      canvas.width = Math.round(viewport.width);
      canvas.height = Math.round(viewport.height);
      const r = page.render({ canvas, canvasContext: ctx, viewport });
      task = r;
      await r.promise;
      if (!cancelled) setReady(true);
    })().catch((e) => {
      if (cancelled || (e as { name?: string } | null)?.name === 'RenderingCancelledException') return;
      console.error('PDFのサムネイルを作れませんでした', e);
      onError();
    });
    return () => {
      cancelled = true;
      task?.cancel();
      // 読み込み用のスレッドも片付ける(残すと開くたびに増え、iPhoneでメモリ不足になる)
      doc?.destroy?.();
    };
  }, [url, onError]);
  return (
    <>
      {!ready && <span className="att-thumb-loading">読み込み中…</span>}
      <canvas ref={canvasRef} className="att-thumb-canvas" style={{ display: ready ? 'block' : 'none' }} />
    </>
  );
}

/** 拡張子(表示できないときの印) */
function kindLabel(file: StoredFile): string {
  const ext = file.name.includes('.') ? file.name.split('.').pop() : '';
  return (ext || file.content_type.split('/').pop() || 'FILE').toUpperCase().slice(0, 5);
}

function Thumb({ file }: { file: StoredFile }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    setUrl(null);
    setFailed(false);
    getFileUrl(file.path)
      .then((u) => alive && setUrl(u))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [file.path]);
  const markFailed = useRef(() => setFailed(true)).current;
  const isPdf = file.content_type === 'application/pdf';
  const isImage = file.content_type.startsWith('image/');

  return (
    <>
      <button type="button" className="att-thumb" onClick={() => setOpen(true)} title={`${file.name}(押すと大きく表示)`}>
        <span className="att-thumb-box">
          {failed || (!isPdf && !isImage) ? (
            <span className="att-thumb-kind">{kindLabel(file)}</span>
          ) : !url ? (
            <span className="att-thumb-loading">読み込み中…</span>
          ) : isPdf ? (
            <PdfThumb url={url} onError={markFailed} />
          ) : (
            <img src={url} alt={file.name} loading="lazy" decoding="async" onError={markFailed} />
          )}
        </span>
        <span className="att-thumb-name">{file.name}</span>
      </button>
      {open && <FilePreviewModal file={file} onClose={() => setOpen(false)} />}
    </>
  );
}

export default function AttachmentThumbs({ files }: { files: StoredFile[] }) {
  return (
    <div className="att-thumbs">
      {files.map((f, i) => (
        <Thumb key={`${f.path}-${i}`} file={f} />
      ))}
    </div>
  );
}
